import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useUser } from "@clerk/react";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { StepType, StepChild, StepSchedule, StepParent } from "@/components/booking/Steps";
import { Confirmation } from "@/components/booking/Confirmation";
import { useApi, errMsg } from "@/lib/api";

const LABELS = ["Visit type", "Your child", "Date & time", "Your details"];
const EMPTY = { type: "clinic", service: "", child_name: "", child_age: "", notes: "", date: "", time: "", parent_name: "", phone: "", email: "" };

const valid = (step, f) => [
  !!f.type,
  f.child_name.trim() && f.child_age.trim() && f.service,
  f.date && f.time,
  f.parent_name.trim() && f.phone.replace(/\D/g, "").length >= 10 && /\S+@\S+\.\S+/.test(f.email),
][step];

const Progress = ({ step }) => (
  <div className="flex gap-2" data-testid="booking-progress">
    {LABELS.map((l, i) => (
      <div key={l} className="flex-1">
        <div className={`h-2.5 rounded-full transition-colors duration-500 ${i <= step ? "bg-teal-600" : "bg-slate-200"}`} />
        <p className={`mt-2 hidden text-xs font-bold sm:block ${i <= step ? "text-teal-700" : "text-slate-400"}`}>{l}</p>
      </div>
    ))}
  </div>
);

export default function Book() {
  const [params] = useSearchParams();
  const { user } = useUser();
  const api = useApi();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ ...EMPTY, type: params.get("type") === "online" ? "online" : "clinic" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (p) => setForm((f) => ({ ...f, ...p }));

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, parent_name: f.parent_name || user.fullName || [user.firstName, user.lastName].filter(Boolean).join(" "), email: f.email || user.primaryEmailAddress?.emailAddress || "" }));
  }, [user]);

  const submit = async () => {
    setBusy(true);
    try { setDone(await api("post", "/bookings", form)); } catch (e) {
      toast.error(errMsg(e));
      if (e?.response?.status === 409) { set({ time: "" }); setStep(2); }
    }
    setBusy(false);
  };

  const Step = [StepType, StepChild, StepSchedule, StepParent][step];
  return (
    <div className="dot-bg min-h-[80vh] px-4 pb-14 pt-10 sm:pt-14" data-testid="book-page">
      <div className="mx-auto max-w-4xl rounded-[2.5rem] border-2 border-slate-800/5 bg-white/95 p-6 shadow-xl sm:p-10">
        {done ? <Confirmation b={done} /> : (
          <>
            <h1 className="text-3xl font-bold sm:text-4xl">{form.type === "online" ? "Book an online consultation" : "Book an appointment"}</h1>
            <p className="mt-2 text-sm text-slate-500">Takes less than a minute. No payment needed now.</p>
            <div className="mt-8"><Progress step={step} /></div>
            <div className="mt-10 min-h-[280px]"><Step form={form} set={set} /></div>
            <div className="mt-10 flex items-center justify-between">
              <button onClick={() => setStep(step - 1)} disabled={step === 0} data-testid="booking-back-button"
                className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-bold text-slate-600 hover:bg-slate-100 disabled:invisible"><ArrowLeft className="h-4 w-4" />Back</button>
              {step < 3 ? (
                <button onClick={() => setStep(step + 1)} disabled={!valid(step, form)} data-testid="booking-next-button"
                  className="btn-pop shadow-chunky inline-flex items-center gap-2 rounded-full bg-teal-600 px-7 py-3.5 text-sm font-bold text-white disabled:opacity-40">Next<ArrowRight className="h-4 w-4" /></button>
              ) : (
                <button onClick={submit} disabled={!valid(3, form) || busy} data-testid="booking-submit-button"
                  className="btn-pop shadow-chunky-coral inline-flex items-center gap-2 rounded-full bg-[#ff6b6b] px-7 py-3.5 text-sm font-bold text-white disabled:opacity-40">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />}Confirm booking</button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
